import React from 'react';
import { Alert, Button, Col, Space, Spin, Typography, message } from 'antd';
import { ArrowLeftOutlined, CameraOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { ControlTowerMetricStrip } from '@/components/control-tower/ControlTowerMetricStrip';
import { ControlTowerMetricTile } from '@/components/control-tower/ControlTowerMetricTile';
import { epcSubcontractAdminService } from '@/services/epcSubcontractAdmin.service';

const { Title, Paragraph, Text } = Typography;

function fmtMoney(currency: string, v: number | undefined) {
  if (v == null || Number.isNaN(Number(v))) return '—';
  return `${currency} ${Number(v).toLocaleString()}`;
}

function apiErr(e: unknown): string {
  const ax = e as { response?: { data?: { message?: string } }; message?: string };
  return ax?.response?.data?.message || ax?.message || 'Request failed';
}

const EpcSubcontractControlPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const towerQ = useQuery({
    queryKey: ['epc-subcontract-control-tower', id],
    queryFn: () => epcSubcontractAdminService.getControlTower(id!),
    enabled: Boolean(id),
    staleTime: 15_000,
  });

  const forecastQ = useQuery({
    queryKey: ['epc-subcontract-forecast', id, 'control'],
    queryFn: () => epcSubcontractAdminService.getForecast(id!),
    enabled: Boolean(id),
    staleTime: 15_000,
  });

  const snapshotM = useMutation({
    mutationFn: () => epcSubcontractAdminService.createForecastSnapshot(id!),
    onSuccess: () => {
      message.success('Forecast snapshot saved');
      qc.invalidateQueries({ queryKey: ['epc-subcontract-control-tower', id] });
      qc.invalidateQueries({ queryKey: ['epc-subcontract-forecast', id] });
    },
    onError: (e: unknown) => message.error(apiErr(e)),
  });

  if (!id) return null;

  if (towerQ.isLoading && forecastQ.isLoading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (towerQ.isError && forecastQ.isError) {
    return (
      <div style={{ padding: 24 }}>
        <Alert
          type="error"
          message={
            (towerQ.error as Error)?.message ||
            (forecastQ.error as Error)?.message ||
            'Failed to load control tower'
          }
        />
      </div>
    );
  }

  const forecast = towerQ.data?.forecast || forecastQ.data;
  const counts = towerQ.data?.counts;
  const currency = String(forecast?.currency || towerQ.data?.currency || 'AED');
  const ld =
    towerQ.data?.ldExposureAdvisory ||
    forecast?.ldExposure ||
    (forecast?.ldExposureAdvisory != null
      ? { exposureAmount: forecast.ldExposureAdvisory }
      : null);
  const doubleWarn = towerQ.data?.doubleCommitmentWarning;

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ marginBottom: 12, width: '100%', justifyContent: 'space-between' }} wrap>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(`/epc/commercial/subcontracts/${id}`)}
        >
          Subcontract workspace
        </Button>
        <Button
          type="primary"
          icon={<CameraOutlined />}
          loading={snapshotM.isPending}
          onClick={() => snapshotM.mutate()}
        >
          Create snapshot
        </Button>
      </Space>
      <Title level={3} style={{ marginBottom: 4 }}>
        Subcontract commercial control
      </Title>
      <Paragraph type="secondary">
        Advisory commercial exposure for this SCA (no GL). Certification remains the bridge to
        draft Supplier Invoice.
      </Paragraph>

      {doubleWarn?.risk ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Double-commitment risk"
          description={
            doubleWarn.policy ||
            'Child PO Subcontract lines may overlap SCA commitment. Sync skips Subcontract category when PO.subcontractor_agreement_id is set.'
          }
        />
      ) : null}

      {forecast ? (
        <ControlTowerMetricStrip zone="attention" title="Commercial forecast">
          <Col xs={12} sm={8} md={6} lg={4}>
            <ControlTowerMetricTile
              title="Contract sum"
              count={0}
              valueDisplay={fmtMoney(currency, forecast.contractSum)}
            />
          </Col>
          <Col xs={12} sm={8} md={6} lg={4}>
            <ControlTowerMetricTile
              title="Approved VOs"
              count={0}
              valueDisplay={fmtMoney(currency, forecast.approvedVariations)}
            />
          </Col>
          <Col xs={12} sm={8} md={6} lg={4}>
            <ControlTowerMetricTile
              title="Certified to date"
              count={0}
              valueDisplay={fmtMoney(currency, forecast.certifiedToDate)}
            />
          </Col>
          <Col xs={12} sm={8} md={6} lg={4}>
            <ControlTowerMetricTile
              title="Claims open"
              count={0}
              valueDisplay={fmtMoney(currency, forecast.claimsOpen)}
              urgency={forecast.claimsOpen > 0 ? 'attention' : 'neutral'}
            />
          </Col>
          <Col xs={12} sm={8} md={6} lg={4}>
            <ControlTowerMetricTile
              title="Retention held"
              count={0}
              valueDisplay={fmtMoney(currency, forecast.retentionHeld)}
            />
          </Col>
          <Col xs={12} sm={8} md={6} lg={4}>
            <ControlTowerMetricTile
              title="Projected final"
              count={0}
              valueDisplay={fmtMoney(currency, forecast.projectedFinal)}
            />
          </Col>
          <Col xs={12} sm={8} md={6} lg={4}>
            <ControlTowerMetricTile
              title="LD exposure"
              count={0}
              valueDisplay={fmtMoney(
                currency,
                Number(
                  (ld as { exposureAmount?: number })?.exposureAmount ??
                    forecast.ldExposureAdvisory ??
                    0
                )
              )}
              urgency={
                Number(
                  (ld as { exposureAmount?: number })?.exposureAmount ??
                    forecast.ldExposureAdvisory ??
                    0
                ) > 0
                  ? 'critical'
                  : 'neutral'
              }
            />
          </Col>
        </ControlTowerMetricStrip>
      ) : (
        <Alert type="warning" showIcon message="Forecast unavailable" style={{ marginBottom: 16 }} />
      )}

      {counts ? (
        <div style={{ marginTop: 16 }}>
          <ControlTowerMetricStrip zone="neutral" title="Work queues">
            <Col xs={12} sm={8} md={6} lg={4}>
              <ControlTowerMetricTile title="SPCs" count={counts.paymentCertificates} />
            </Col>
            <Col xs={12} sm={8} md={6} lg={4}>
              <ControlTowerMetricTile title="Certifications" count={counts.certifications} />
            </Col>
            <Col xs={12} sm={8} md={6} lg={4}>
              <ControlTowerMetricTile
                title="Variations"
                count={counts.variations}
                urgency={counts.variations > 0 ? 'attention' : 'neutral'}
              />
            </Col>
            <Col xs={12} sm={8} md={6} lg={4}>
              <ControlTowerMetricTile
                title="Claims"
                count={counts.claims}
                urgency={counts.claims > 0 ? 'attention' : 'neutral'}
              />
            </Col>
            <Col xs={12} sm={8} md={6} lg={4}>
              <ControlTowerMetricTile title="EOT" count={counts.eotRequests} />
            </Col>
            <Col xs={12} sm={8} md={6} lg={4}>
              <ControlTowerMetricTile title="Closeouts" count={counts.closeouts} />
            </Col>
          </ControlTowerMetricStrip>
        </div>
      ) : null}

      {towerQ.data?.overdueNotices?.length ? (
        <Paragraph style={{ marginTop: 16 }}>
          <Text type="danger">{towerQ.data.overdueNotices.length} overdue notice(s)</Text>
        </Paragraph>
      ) : null}
    </div>
  );
};

export default EpcSubcontractControlPage;
